import { Card, CardBody, Button } from '@components/ui';
import type { InvoiceWizardData } from '../../types/invoiceWizard.types';

interface StepReferenceProps {
  wizardData: InvoiceWizardData;
  onNext: () => void;
  onPrevious: () => void;
}

export function StepReference({ wizardData, onNext, onPrevious }: StepReferenceProps) {
  const { gDFRef, documentMode } = wizardData;

  // Si no hay referencia, no mostrar este step
  if (!gDFRef || documentMode === 'invoice') {
    onNext();
    return null;
  }

  const documentTypeName = documentMode === 'credit-note' ? 'Nota de Crédito' : 'Nota de Débito';

  return (
    <div className="space-y-6" data-testid="invoice-wizard-reference-container">
      <Card>
        <CardBody className="p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-secondary-900 dark:text-white">
              Documento Referenciado
            </h2>
            <p className="mt-2 text-secondary-600 dark:text-secondary-400">
              Esta {documentTypeName.toLowerCase()} hace referencia a la siguiente factura electrónica
            </p>
          </div>

          <div className="space-y-6">
            {/* Información del documento referenciado */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Número de Factura */}
              <div className="rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/50 p-4">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Número de Factura Interna Electrónica
                </label>
                <p className="text-lg font-semibold text-secondary-900 dark:text-white">
                  {gDFRef.dNroFacIE || 'N/A'}
                </p>
              </div>

              {/* Fecha del Documento */}
              <div className="rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/50 p-4">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Fecha del Documento Referenciado
                </label>
                <p className="text-lg font-semibold text-secondary-900 dark:text-white">
                  {gDFRef.dFechaDFRef
                    ? new Date(gDFRef.dFechaDFRef).toLocaleDateString('es-PA', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : 'N/A'}
                </p>
              </div>

              {/* Número de Factura en Papel (si aplica) */}
              {gDFRef.dNroFacPap && (
                <div className="rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/50 p-4">
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Número de Factura en Papel
                  </label>
                  <p className="text-lg font-semibold text-secondary-900 dark:text-white">
                    {gDFRef.dNroFacPap}
                  </p>
                </div>
              )}
            </div>

            {/* CUFE del Documento Referenciado */}
            <div className="rounded-lg border border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 p-4">
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                CUFE del Documento Referenciado
              </label>
              <p className="text-sm font-mono text-primary-900 dark:text-primary-100 break-all">
                FE{gDFRef.dCufeRef}
              </p>
              <p className="mt-2 text-xs text-primary-600 dark:text-primary-400">
                Este es el Código Único de Factura Electrónica (CUFE) que identifica de forma única
                el documento original que está siendo referenciado.
              </p>
            </div>

            {/* Información adicional */}
            <div className="rounded-lg border border-info-200 dark:border-info-700 bg-info-50 dark:bg-info-900/20 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-info-600 dark:text-info-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-info-800 dark:text-info-200">
                    Información Importante
                  </h3>
                  <div className="mt-2 text-sm text-info-700 dark:text-info-300">
                    <ul className="list-disc list-inside space-y-1">
                      <li>
                        Esta {documentTypeName.toLowerCase()} heredará los datos del cliente y los
                        items de la factura original
                      </li>
                      <li>
                        Podrá modificar las cantidades y agregar/eliminar items según sea necesario
                      </li>
                      <li>
                        La referencia al documento original quedará registrada en el sistema DGI
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6">
        <Button variant="outline" onClick={onPrevious} data-testid="invoice-wizard-reference-previous-button">
          Anterior
        </Button>
        <Button variant="primary" onClick={onNext} data-testid="invoice-wizard-reference-continue-button">
          Continuar
        </Button>
      </div>
    </div>
  );
}
